export default class sdate {
  #date: string;

  /**
   * Creates a new date object.
   * @param {string} [dateString] - A date string in YYYY-MM-DD format. If not provided, the current date is used.
   */
  constructor(dateString?: string | Date) {
    if (dateString && typeof dateString === "string") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        throw new Error("Invalid date format. Please use YYYY-MM-DD.");
      }
      this.#date = dateString;
      return;
    }
    if (dateString && dateString instanceof Date) {
      const date = new Date();
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      this.#date = `${year}-${String(month).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;
      return;
    }

    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    this.#date = `${year}-${String(month).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;
  }

  /**
   * Adds a specified number of days to the date.
   * @param {number} days - The number of days to add.
   * @returns {string} The new date in YYYY-MM-DD format.
   */
  addDays(days: number): sdate {
    const dateObj = new Date(this.#date);
    dateObj.setDate(dateObj.getDate() + days);
    return new sdate(makeTDate(dateObj));
  }

  /**
   * Adds a specified number of months to the date.
   * @param {number} months - The number of months to add.
   * @returns {string} The new date in YYYY-MM-DD format.
   */
  addMonths(months: number): sdate {
    const dateObj = new Date(this.#date);
    dateObj.setMonth(dateObj.getMonth() + months);
    return new sdate(makeTDate(dateObj));
  }

  /**
   * Adds a specified number of years to the date.
   * @param {number} years - The number of years to add.
   * @returns {string} The new date in YYYY-MM-DD format.
   */
  addYears(years: number): sdate {
    const dateObj = new Date(this.#date);
    dateObj.setFullYear(dateObj.getFullYear() + years);
    return new sdate(makeTDate(dateObj));
  }

  /**
   * Calculates the difference in days between two dates.
   * @param {sdate} anotherDate - The date to compare to.
   * @returns {number} The difference in days.
   */
  difference(anotherDate: sdate): number {
    const date1 = new Date(this.#date);
    const date2 = new Date(anotherDate.toString());
    const diffTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Formats the date as DD/MM/YYYY.
   * @returns {string} The formatted date.
   */
  fDate(): string {
    const [year, month, day] = this.#date.split("-");
    return `${day}/${month}/${year}`;
  }

  /**
   * Checks if two date objects are equal.
   * @param {sdate | Date} anotherDate - The date to compare to.
   * @returns {boolean} True if the dates are equal, false otherwise.
   */
  equals(anotherDate: sdate | Date): boolean {
    if (anotherDate instanceof Date) {
      return makeTDate(anotherDate) === this.toString();
    }
    return this.toString() === anotherDate.toString();
  }

  /**
   * Returns the date as a string in YYYY-MM-DD format.
   * @returns {string} The date string.
   */
  toString(): string {
    return this.#date;
  }

  /**
   * Returns the year of the date.
   * @returns {number} The year.
   */
  year(): number {
    return new Date(this.#date).getUTCFullYear();
  }

  /**
   * Returns the last two digits of the year.
   * @returns {string} The short year.
   */
  yearShort(): string {
    return String(this.year()).slice(-2);
  }

  /**
   * Returns the month of the date.
   * @returns {number} The month.
   */
  month(): number {
    return new Date(this.#date).getMonth() + 1;
  }

  /**
   * Returns the month of the date, padded with a leading zero if necessary.
   * @returns {string} The padded month.
   */
  monthPad(): string {
    return String(this.month()).padStart(2, "0");
  }

  /**
   * Returns the day of the month.
   * @returns {number} The day.
   */
  day(): number {
    return new Date(this.#date).getDate();
  }

  /**
   * Returns the day of the month, padded with a leading zero if necessary.
   * @returns {string} The padded day.
   */
  dayPad(): string {
    return String(this.day()).padStart(2, "0");
  }

  /**
   * Returns the day of the week (0 for Sunday, 1 for Monday, etc.).
   * @returns {number} The day of the week.
   */
  dayOfWeek(): number {
    return new Date(this.#date).getDay();
  }

  /**
   * Returns an object with the year, month, and day of the date.
   * @returns {{year: number, month: number, day: number}} The year, month, and day.
   */
  ymd(): { year: number; month: number; day: number } {
    return {
      year: this.year(),
      month: this.month(),
      day: this.day(),
    };
  }

  /**
   * Returns an object with the year, month, day, day of the week, and tDate of the date.
   * @returns {{year: number, month: number, day: number, dayOfWeek: number, tDate: string}} The date information.
   */
  ymddt(): {
    year: number;
    month: number;
    day: number;
    dayOfWeek: number;
    tDate: string;
  } {
    return {
      year: this.year(),
      month: this.month(),
      day: this.day(),
      dayOfWeek: this.dayOfWeek(),
      tDate: this.toString(),
    };
  }

  /**
   * Returns the date of the start of the week (Monday) for the given date.
   * @returns {sdate} The date of the start of the week.
   */
  startOfWeek(): sdate {
    const dateObj = new Date(this.#date);
    const day = dateObj.getDay();
    const diff = dateObj.getDate() - day + (day === 0 ? -6 : 1);
    dateObj.setDate(diff);
    const year = dateObj.getUTCFullYear();
    const month = dateObj.getUTCMonth() + 1;
    const newDay = dateObj.getUTCDate();
    return new sdate(
      `${year}-${String(month).padStart(2, "0")}-${String(newDay).padStart(
        2,
        "0"
      )}`
    );
  }

  /**
   * Checks to see if the sdate is in an Array of dates
   * @param {string[] | sdate[] | Date[]} arrayToCheck the array of dates to check
   * @returns {boolean} true if the date is in the array
   */
  isInArray(arrayToCheck: string[] | sdate[] | Date[]): boolean {
    if (arrayToCheck.length === 0) return false;
    const first = arrayToCheck[0];
    if (typeof first === "string") {
      return (arrayToCheck as string[]).includes(this.toString());
    }
    if (first instanceof Date) {
      return (arrayToCheck as Date[]).some(
        (d) => makeTDate(d) === this.toString()
      );
    }
    if (first instanceof sdate) {
      return (arrayToCheck as sdate[]).some(
        (e) => e.toString() === this.toString()
      );
    }
    return false;
  }

  /**
   * Checks to see if the date is in this month or a given month
   * @param {number} year optional, the year to check
    * @param {number} month optional, the month to check
   * @returns {boolean} true if the date is in month
   */
  inMonth(year: number = new Date().getFullYear(), month: number = new Date().getMonth() + 1): boolean {
    let [y, m] = this.#date.split("-");
    if (parseInt(y) === year && parseInt(m) === month) return true;
    return false;
  }
  isToday(): boolean {
    if(this.#date===new sdate().toString()) return true;
    return false;
  }
  [Symbol.for("nodejs.util.inspect.custom")]() {
    return this.toString();
  }
}

function makeTDate(dt:Date):string { 
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`
}
